/**
 * Single facet dropdown — Deakin filter-toolbar-dropdown pattern.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  type ChangeEvent,
} from 'react';
import { SEARCH_URL_FACET_KEY_PREFIX } from '../../context/search-app/search-url';
import {
  applyFilterDropdownWidth,
  ensureFilterDropdownVisible,
  resetFilterDropdownPosition,
} from './filter-dropdown-position';
import {
  deakinFacultyLabel,
  deakinTopicLabel,
} from '../../funnelback/deakin-config';

export type FunnelbackFacetValue = {
  data: string;
  label: string;
  count: number;
  selected: boolean;
  toggleUrl: string;
};

export type FunnelbackFacetFilterFacet = {
  name: string;
  selectionType?: string;
  allValues?: FunnelbackFacetValue[];
};

type Props = {
  facet: FunnelbackFacetFilterFacet;
  draftValues: string[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDraftChange: (values: string[]) => void;
  onApply: () => void;
};

function facetNameClassToken(name: string): string {
  const t = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return t || 'facet';
}

function parseAllFacetIdsFromToggleUrl(toggleUrl: string): string[] {
  const qIdx = toggleUrl.indexOf('?');
  const qs = qIdx >= 0 ? toggleUrl.slice(qIdx) : toggleUrl;
  const params = new URLSearchParams(qs.startsWith('?') ? qs.slice(1) : qs);
  const ids: string[] = [];
  params.forEach((_value, key) => {
    if (key.startsWith(SEARCH_URL_FACET_KEY_PREFIX)) {
      ids.push(key.slice(SEARCH_URL_FACET_KEY_PREFIX.length));
    }
  });
  return ids;
}

function facetDisplayNameMatchesId(
  facetDisplayName: string,
  facetId: string,
): boolean {
  const nameLower = facetDisplayName.trim().toLowerCase();
  const idPrefix = facetId.split('|')[0]?.trim().toLowerCase() ?? '';
  return nameLower.length > 0 && idPrefix === nameLower;
}

function pickFacetIdFromToggleUrlCandidates(
  facetIds: string[],
  facetDisplayName: string,
): string | null {
  if (facetIds.length === 0) {
    return null;
  }

  const nameLower = facetDisplayName.trim().toLowerCase();
  const byDisplayName = facetIds.find(
    (id) => id.split('|')[0]?.trim().toLowerCase() === nameLower,
  );
  if (byDisplayName) {
    return byDisplayName;
  }

  if (facetIds.length === 1) {
    return facetDisplayNameMatchesId(facetDisplayName, facetIds[0])
      ? facetIds[0]
      : null;
  }

  return null;
}

export function resolveFacetId(
  facet: FunnelbackFacetFilterFacet,
): string | null {
  const values = facet.allValues;
  if (!Array.isArray(values)) return null;
  const name = facet.name ?? '';

  const candidateIds = new Set<string>();
  for (const v of values) {
    if (!v.toggleUrl) {
      continue;
    }
    for (const id of parseAllFacetIdsFromToggleUrl(v.toggleUrl)) {
      candidateIds.add(id);
    }
  }

  for (const id of candidateIds) {
    if (facetDisplayNameMatchesId(name, id)) {
      return id;
    }
  }

  for (const v of values) {
    if (!v.toggleUrl) {
      continue;
    }
    const facetIds = parseAllFacetIdsFromToggleUrl(v.toggleUrl);
    const id = pickFacetIdFromToggleUrlCandidates(facetIds, name);
    if (id) {
      return id;
    }
  }

  return null;
}

export function facetHasSelectableOptions(
  facet: FunnelbackFacetFilterFacet,
): boolean {
  const values = facet.allValues;
  if (!Array.isArray(values) || values.length === 0) {
    return false;
  }
  return resolveFacetId(facet) !== null;
}

const FacetFilterDropdown = ({
  facet,
  draftValues,
  isOpen,
  onOpenChange,
  onDraftChange,
  onApply,
}: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownId = useId();
  const selectAllId = useId();
  const allValues = facet.allValues ?? [];
  const isMultiple = facet.selectionType !== 'SINGLE';
  const nameToken = facetNameClassToken(facet.name);
  const displayName = facet.name.trim();

  const allOptionData = useMemo(
    () => allValues.map((v) => v.data).filter(Boolean),
    [allValues],
  );

  const allSelected =
    isMultiple &&
    allOptionData.length > 0 &&
    allOptionData.every((data) => draftValues.includes(data));

  const toggleOpen = useCallback(() => {
    onOpenChange(!isOpen);
  }, [isOpen, onOpenChange]);

  const handleSelectAll = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onDraftChange(e.target.checked ? [...allOptionData] : []);
    },
    [allOptionData, onDraftChange],
  );

  const handleCheckboxChange = useCallback(
    (data: string, checked: boolean) => {
      if (isMultiple) {
        const next = checked
          ? [...new Set([...draftValues, data])]
          : draftValues.filter((v) => v !== data);
        onDraftChange(next);
        return;
      }
      onDraftChange(checked ? [data] : []);
    },
    [draftValues, isMultiple, onDraftChange],
  );

  const handleApply = useCallback(() => {
    onApply();
    onOpenChange(false);
  }, [onApply, onOpenChange]);

  useLayoutEffect(() => {
    const panel = dropdownRef.current;
    const trigger = triggerRef.current;
    if (!panel) {
      return;
    }

    if (!isOpen) {
      resetFilterDropdownPosition(panel);
      return;
    }

    if (trigger && rootRef.current) {
      ensureFilterDropdownVisible(rootRef.current, trigger, panel);
    }
  }, [isOpen, allValues.length]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (rootRef.current && target && !rootRef.current.contains(target)) {
        onOpenChange(false);
      }
    };

    const onResize = () => {
      const panel = dropdownRef.current;
      const root = rootRef.current;
      if (panel && root) {
        applyFilterDropdownWidth(root, panel);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, [isOpen, onOpenChange]);

  if (!facetHasSelectableOptions(facet)) {
    return null;
  }

  const listClassName = 'filter-group-list';

  return (
    <div
      ref={rootRef}
      className={`filter-toolbar-dropdown filter-toolbar-dropdown__${nameToken}`}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-pressed={isOpen}
        aria-controls={dropdownId}
        aria-expanded={isOpen}
        onClick={toggleOpen}
      >
        <span className="filter-button-label">{displayName}</span>
      </button>
      <div
        ref={dropdownRef}
        id={dropdownId}
        className={`filter-dropdown${isOpen ? ' filter-dropdown--open' : ''}`}
        style={{ display: isOpen ? 'flex' : 'none' }}
      >
        {isMultiple ? (
          <ul className="filter-group-list">
            <li className="select-all">
              <input
                type="checkbox"
                id={selectAllId}
                className="checkbox-all"
                checked={allSelected}
                onChange={handleSelectAll}
              />
              <label htmlFor={selectAllId}>Select all</label>
            </li>
          </ul>
        ) : null}
        <div className="filter-dropdown__scroll">
          <ul className={listClassName}>
            {allValues.map((value, index) => {
              const inputId = `${dropdownId}-${index}`;
              const checked = draftValues.includes(value.data);
              const label =
                facet.name === 'Topics'
                  ? deakinTopicLabel(value.data)
                  : facet.name === 'Faculty'
                    ? deakinFacultyLabel(value.data)
                    : value.label || value.data;
              return (
                <li key={`${value.data}-${index}`}>
                  <input
                    type={isMultiple ? 'checkbox' : 'radio'}
                    id={inputId}
                    name={isMultiple ? undefined : dropdownId}
                    value={value.data}
                    checked={checked}
                    onChange={(e) =>
                      handleCheckboxChange(value.data, e.target.checked)
                    }
                  />
                  <label htmlFor={inputId}>{label}</label>
                </li>
              );
            })}
          </ul>
        </div>
        <button
          type="button"
          className="btn btn--charcoal"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default FacetFilterDropdown;
